import { TextField } from "~/common/mui";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { SchaefflerFormSelectFieldDTO } from "~/features/cms/types/dto";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { useResolveValue } from "~/features/cms/schaefflerForm/schaefflerFormHelper";
import { makeExternallyStyledElement } from "~/features/cms/utils/styles";

export type SchaefflerFormSelectFieldProps = Omit<SchaefflerFormSelectFieldDTO, "type"> & {
  className?: string;
};

export const SchaefflerFormSelectField = (props: SchaefflerFormSelectFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const options = props.values.map((value) => ({
    value: value.sfValue,
    label: t(value.label),
  }));

  options.sort((a, b) => a.label.localeCompare(b.label));

  const initialValue = useResolveValue(props.initialValue ?? "");
  const initialSfValue = props.values.find((value) => value.value === initialValue)?.sfValue ?? "";

  return (
    <Controller
      control={control}
      name={props.name}
      defaultValue={initialSfValue}
      rules={{
        required: {
          value: props.required,
          message: t("page.scandmc.form.error.required"),
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <ExternallyStyledTextField
          rootStyles={props.rootStyles}
          select
          required={props.required}
          label={t(props.title)}
          {...field}
          error={!!error}
          helperText={error?.message}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </ExternallyStyledTextField>
      )}
    />
  );
};

const ExternallyStyledTextField = makeExternallyStyledElement(TextField);
